/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css, jsx } from '@compiled/react';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button/new';
import FilterIcon from '@atlaskit/icon/core/filter';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { SpotlightPulse } from '@atlaskit/onboarding';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';

import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { JiraJqlScopeInput } from '@atlassian/jira-relay/src/__generated__/FilterPopupContentQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import {
	fireUIAnalytics,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { ChoreographerContextProvider } from '@atlassian/jira-choreographer-services/src/ChoreographerContextProvider.tsx';
import { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import { useJQL } from '../../controllers/ast/index.tsx';
import { useNumberOfSelectedValues } from '../../controllers/ast/use-selected-values/index.tsx';
import { SelectedFieldContainer } from '../../controllers/use-selected-field/index.tsx';
import FallbackSkeleton from './fallback-skeleton/index.tsx';
import { filterPopupContentEntryPoint } from './filter-popup-content/entrypoint.tsx';
import messages from './messages.tsx';
import { ErrorMessage } from './filter-popup-content/ui/error-message/index.tsx';
import type { PopupHeight, PopupWidth } from './types.tsx';
import KeyboardShortcuts from './keyboard-shortcuts/index.tsx';

const POPUP_HEIGHT: PopupHeight = '380px';
const POPUP_WIDTH: PopupWidth = '600px';

type Props = {
	scope?: JiraJqlScopeInput;
	changeboardingMessageId?: string;
	isSoftwareFullTheming?: boolean;
};

export const FilterPopup = ({ scope, changeboardingMessageId, isSoftwareFullTheming }: Props) => {
	const jql = useJQL();
	const numberOfSelectedValues = useNumberOfSelectedValues();
	const { formatMessage } = useIntl();
	const [isFeedbackOpen, setFeedbackIsOpen] = useState(false);

	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const cloudId = useCloudId();
	const { data: orgId } = useOrgId();

	const coordinationClient = useMemo(
		() =>
			changeboardingMessageId
				? new CoordinationClient(cloudId, '/gateway/api', false, orgId, 'engagement')
				: undefined,
		[changeboardingMessageId, cloudId, orgId],
	);

	// TODO: Remove this once we have a new changeboarding flow and gate filter-refinement-changeboarding-second-page is rolled out
	const [isMessageActive, setIsMessageActive] = useState(false);
	// These are introduced with the filter-refinement-changeboarding-second-page gate for the new changeboarding flow
	const [activeSpotlight, setActiveSpotlight] = useState<null | number>(null);
	const startSpotlight = useCallback(
		(isActive: boolean) => setActiveSpotlight(isActive ? 0 : null),
		[],
	);
	const nextSpotlight = useCallback(
		() => setActiveSpotlight((activeSpotlight || 0) + 1),
		[activeSpotlight],
	);
	const endSpotlight = useCallback(() => setActiveSpotlight(null), []);

	useEffect(() => {
		if (changeboardingMessageId) {
			coordinationClient
				?.start(changeboardingMessageId)
				.then((isActive) =>
					fg('filter-refinement-changeboarding-second-page')
						? startSpotlight(isActive)
						: setIsMessageActive(isActive),
				)
				.catch(() =>
					fg('filter-refinement-changeboarding-second-page')
						? endSpotlight()
						: setIsMessageActive(false),
				);
		}
	}, [coordinationClient, changeboardingMessageId, startSpotlight, endSpotlight]);

	const closeChangeboarding = useCallback(
		(event: React.MouseEvent<Element, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			// Stop propagation avoid closing filter popup when dismissing changeboarding
			event.stopPropagation();

			if (changeboardingMessageId) {
				coordinationClient?.stop(changeboardingMessageId);
				fg('filter-refinement-changeboarding-second-page')
					? endSpotlight()
					: setIsMessageActive(false);
				fireUIAnalytics(analyticsEvent, 'filterRefinementChangeboardingClosed');
			}
		},
		[coordinationClient, changeboardingMessageId, endSpotlight],
	);

	const runtimeProps = useMemo(
		() => ({
			onCloseChangeboarding: closeChangeboarding,
			isMessageActive,
			activeSpotlight,
			changeboardingMessageId,
			onTriggerNextSpotlight: nextSpotlight,
			onTriggerEndSpotlight: endSpotlight,
		}),
		[
			closeChangeboarding,
			isMessageActive,
			activeSpotlight,
			changeboardingMessageId,
			nextSpotlight,
			endSpotlight,
		],
	);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			jqlContext: jql,
			scope,
			onFeedbackClick: setFeedbackIsOpen,
		}),
		[cloudId, jql, scope, setFeedbackIsOpen],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		filterPopupContentEntryPoint,
		entryPointParams,
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const onClick = useCallback(
		(_: React.MouseEvent<HTMLButtonElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			setIsOpen((open) => !open);
			fireUIAnalytics(analyticsEvent, 'filter');
		},
		[],
	);

	const onKeyboardShortcutTriggered = useCallback(() => {
		entryPointActions.load();
		setIsOpen((open) => !open);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'pressed',
				actionSubject: 'keyboardShortcut',
			}),
			'filterPopup',
		);
	}, [createAnalyticsEvent, entryPointActions]);

	const onClose = useCallback(() => {
		// don't close the popup when the feedback modal is still open
		!isFeedbackOpen && setIsOpen(false);
	}, [isFeedbackOpen]);

	const formattedTooltipContent = useMemo(() => {
		return (
			<Box xcss={tooltipContentStyles}>
				<FormattedMessage
					{...messages.tooltipMessage}
					values={{
						inline: (parts: React.ReactNode[]) => (
							<Inline xcss={keyboardShortcutTagStyles}>{parts}</Inline>
						),
					}}
				/>
			</Box>
		);
	}, []);

	const trigger = useCallback(
		(triggerProps: TriggerProps) => (
			<SpotlightPulse
				radius={3}
				pulse={
					fg('filter-refinement-changeboarding-second-page')
						? activeSpotlight !== null && activeSpotlight !== 1
						: isMessageActive
				}
			>
				<Tooltip content={formattedTooltipContent}>
					{(tooltipProps) => (
						<Button
							interactionName="filter-popup-load-content"
							{...tooltipProps}
							{...triggerProps}
							iconBefore={FilterIcon}
							iconAfter={() =>
								numberOfSelectedValues > 0 && (
									<Box as="span" paddingInlineEnd="space.050" paddingInlineStart="space.025">
										{isSoftwareFullTheming && fg('jira_theming_milestone_1_fast_follows') ? (
											<div
												css={themedBadgeStyles}
												data-testid="filter-refinement.ui.filter-popup.badge"
											>
												{numberOfSelectedValues}
											</div>
										) : (
											<Badge appearance="primary" testId="filter-refinement.ui.filter-popup.badge">
												{numberOfSelectedValues}
											</Badge>
										)}
									</Box>
								)
							}
							isSelected={isOpen || numberOfSelectedValues > 0}
							ref={mergeRefs(triggerRef, triggerProps.ref)}
							onClick={onClick}
							testId="filter-refinement.ui.filter-popup.button"
						>
							{formatMessage(messages.filterButton)}
						</Button>
					)}
				</Tooltip>
			</SpotlightPulse>
		),
		[
			isMessageActive,
			activeSpotlight,
			isOpen,
			triggerRef,
			onClick,
			formatMessage,
			numberOfSelectedValues,
			formattedTooltipContent,
			isSoftwareFullTheming,
		],
	);

	const clickRetry = useCallback(async () => {
		// opening and closing the popup to trigger the content to re-render if the retry is successful
		// otherwise content will remain showing errorFallback
		setIsOpen(false);
		entryPointActions.unload();
		await entryPointActions.load();
		setIsOpen(true);
	}, [entryPointActions]);

	const content = useCallback(
		() => (
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="jira-filter-refinement-filter-popup-content"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
				errorFallback={() => (
					<Box xcss={containerStyles}>
						<ErrorMessage onClick={() => clickRetry()} errorType="fields" />
					</Box>
				)}
				fallback={<FallbackSkeleton />}
				runtimeProps={runtimeProps}
			/>
		),
		[clickRetry, entryPointReferenceSubject, runtimeProps],
	);

	return (
		<SelectedFieldContainer>
			<ChoreographerContextProvider
				isChoreographed={coordinationClient?.enableChoreographer ?? false}
			>
				<KeyboardShortcuts onShortcutTriggered={onKeyboardShortcutTriggered} />
				<Popup
					isOpen={isOpen}
					onClose={onClose}
					trigger={trigger}
					content={content}
					placement="bottom-start"
					// unfortunately if we render to parent we are limited by page content z-index which is lower
					// than sidenav z-index and will make the popup appear below the sidenav on small screens
					// see https://jplat.atlassian.net/browse/EM-9105
					shouldRenderToParent={false}
					testId="filter-refinement.ui.filter-popup"
					messageId="filter-refinement.ui.filter-popup.popup"
					messageType="transactional"
					// shouldUseCaptureOnOutsideClick allow popup to close by triggering onClose function
					// when clicking an atlaskit dropdown (known bug)
					// https://atlassian.slack.com/archives/CFJ9DU39U/p1704237711295599?thread_ts=1703624547.647969&cid=CFJ9DU39U
					shouldUseCaptureOnOutsideClick
				/>
			</ChoreographerContextProvider>
		</SelectedFieldContainer>
	);
};

const containerStyles = xcss({
	height: POPUP_HEIGHT,
	width: POPUP_WIDTH,
	color: 'color.text.subtle',
});

const keyboardShortcutTagStyles = xcss({
	display: 'inline-flex',
	height: '16px',
	padding: 'space.050',
	justifyContent: 'center',
	alignItems: 'center',
	gap: 'space.075',
	color: 'color.text.inverse',
	borderColor: 'color.border.inverse',
	borderRadius: '3px',
	borderWidth: '1px',
	borderStyle: 'solid',
	textAlign: 'center',
});

const tooltipContentStyles = xcss({
	textAlign: 'center',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	columnGap: 'space.050',
	paddingInline: 'space.075',
	paddingBlock: 'space.025',
});

const themedBadgeStyles = css({
	borderRadius: token('border.radius'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundColor: Tokens.COLOR_BACKGROUND_ACCENT_SELECTED,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	color: Tokens.COLOR_TEXT_ACCENT_SELECTED,
	paddingInline: token('space.050'),
	minWidth: token('space.200'),
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
	textAlign: 'center',
	font: token('font.body.small'),
	display: 'inline-flex',
	justifyContent: 'center',
	blockSize: 'min-content',
});
